import { createStore } from 'zustand';
import { ISubmission, IAssignment } from '@/components/interfaces';
import * as Interfaces from '../types';

// AIDEV-NOTE: pendingSyncs key is `${assignmentId}:${studentId}`, value is click timestamp (ms).
// In-memory only — survives the table's lifetime, lost on reload. See PLAT-17068.
export const pendingSyncKey = (assignmentId: number, studentId: number) => (
  `${assignmentId}:${studentId}`
);

interface GradesTableStoreVariables {
  allLessons: boolean;
  assignment: IAssignment;
  assignments: IAssignment[];
  assignmentsAssigneeModels: Interfaces.IAssignmentAssigneeModel[];
  assignmentTypeFilterOptions: Interfaces.IAssignmentTypeFilterOption[];
  classroom: Interfaces.IClassroom;
  clearSyncPending: (assignmentId: number, studentId: number) => void;
  filters: Interfaces.Filters;
  frameworkNoun: Interfaces.FrameworkNoun;
  gradeSyncEvents: Interfaces.GradeSyncEventsByPair;
  isImpersonating: boolean;
  isLoading: () => boolean;
  isLoadingStudents: boolean;
  isLoadingAssignments: boolean;
  isLoadingSubmissions: boolean;
  isSysadmin: boolean;
  lesson: Interfaces.ILesson;
  markSyncPending: (assignmentId: number, studentId: number) => void;
  navigationParams: () => Record<string, string | number>;
  pendingSyncs: Interfaces.PendingSyncs;
  programId: number;
  showBenchmarksButton: boolean;
  showLMSSyncStatus: boolean;
  showPercentages: boolean;
  stafferId: number;
  students: Interfaces.IStudent[];
  submissions: ISubmission[];
  unit: Interfaces.IUnit;
  updateSubmission: (submission: ISubmission) => void;
  lowScoresSettings: Interfaces.LowScoreSettings;
}

export interface UseGradesTableState extends GradesTableStoreVariables {
  setAssignment: (assignment: IAssignment) => void;
  setFilter: (filter) => void;
  setStore: (nextState: Partial<GradesTableStoreVariables>) => void;
}

const createGradesTableStore = (props: Interfaces.AssignmentsBasedGradesTableProps) => (
  createStore<UseGradesTableState>()((set, get) => ({
    ...props,
    assignment: null,
    assignments: [],
    assignmentsAssigneeModels: [],
    clearSyncPending: (assignmentId, studentId) => set((prev) => {
      const key = pendingSyncKey(assignmentId, studentId);
      if (prev.pendingSyncs[key] === undefined) return prev;

      const nextPending = { ...prev.pendingSyncs };
      delete nextPending[key];

      return { ...prev, pendingSyncs: nextPending };
    }),
    filters: {
      assignmentType: [],
      completionStatus: [],
      gradingStatus: [],
    },
    gradeSyncEvents: {},
    isLoading: () => {
      const { isLoadingAssignments, isLoadingStudents } = get();
      return isLoadingAssignments || isLoadingStudents;
    },
    isLoadingAssignments: true,
    isLoadingStudents: true,
    // AIDEV-NOTE: first load only — useStudentSubmissions clears it on data or error and never sets it
    // back, so background refetches keep showing the scores already on screen instead of re-skeletoning.
    isLoadingSubmissions: true,
    markSyncPending: (assignmentId, studentId) => set(prev => ({
      ...prev,
      pendingSyncs: {
        ...prev.pendingSyncs,
        [pendingSyncKey(assignmentId, studentId)]: Date.now(),
      },
    })),
    navigationParams: () => {
      const { allLessons, classroom, lesson, unit } = get();
      const lessonOrUnitId = () => {
        if (allLessons) return { lesson_id: 'all' };
        return lesson ? { lesson_id: lesson.id } : { unit_id: unit.id };
      };

      return {
        classroom_id: classroom.id,
        ...lessonOrUnitId(),
      };
    },
    pendingSyncs: {},
    setAssignment: assignment => set({ assignment }),
    setFilter: filter => set(prevState => ({ ...prevState, filters: { ...prevState.filters, ...filter } })),
    setStore: nextState => set(prevState => ({ ...prevState, ...nextState })),
    showBenchmarksButton: props.showBenchmarksButton || false,
    students: [],
    submissions: [],
    updateSubmission: submission => (
      set(prev => ({
        ...prev,
        submissions: get().submissions.map((s) => {
          if (s.id !== submission.id) return s;

          return submission;
        }),
      }))
    ),
  }))
);

export default createGradesTableStore;
